import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BaseEntity } from "../../types/baseEntity";
import BaseEntityService from "../../services/baseEntityService";
import { SystemEntityCollections } from "../../shared/enums/shared-enums";
import { useState } from "react";
import { constructFullId } from "../../common/entities/EntityUtils";
import { enqueueSnackbar, SnackbarAction } from "notistack";

type AnyObj = Record<string, any>;

const isPlainObject = (v: any): v is AnyObj =>
  v !== null &&
  typeof v === "object" &&
  (Object.getPrototypeOf(v) === Object.prototype ||
    Object.getPrototypeOf(v) === null);

function defaultsDeep<T>(target: T, defaults: T): T {
  // If target is null/undefined, return a clone of defaults
  if (target == null) return clone(defaults);

  // If defaults is null/undefined, return target
  if (defaults == null) return target;

  // Arrays: if target exists, keep it; if missing, use defaults
  if (Array.isArray(target) || Array.isArray(defaults)) {
    return (target ?? clone(defaults)) as T;
  }

  // Objects: recursively fill missing keys
  if (isPlainObject(target) && isPlainObject(defaults)) {
    const out: AnyObj = { ...clone(defaults), ...clone(target) };
    // Above line fills shallow defaults, then target overwrites shallowly.
    // Now recursively fill nested objects where both sides are objects.
    for (const key of Object.keys(defaults)) {
      const tVal = (target as AnyObj)[key];
      const dVal = (defaults as AnyObj)[key];

      if (tVal === undefined) {
        out[key] = clone(dVal);
      } else if (isPlainObject(tVal) && isPlainObject(dVal)) {
        out[key] = defaultsDeep(tVal, dVal);
      }
    }
    return out as T;
  }

  // Primitives/functions/etc: keep target
  return target;
}

function clone<T>(v: T): T {
  // structuredClone is best when available (modern browsers + Node 17+)
  if (typeof structuredClone === "function") return structuredClone(v);
  // fallback for JSON-like data
  return JSON.parse(JSON.stringify(v));
}

/**
 * Extracts a user-friendly error message from an API error response.
 * Handles Axios errors with server responses and falls back to generic messages.
 */
export const getErrorMessage = (
  error: Error | AxiosError,
  fallbackMessage: string
): string => {
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return fallbackMessage;
};

export type Pagination = { pageIndex: number; pageSize: number; sort?: string };

export interface EntityQueryResponse<T> {
  data: T[];
  total: number;
}

export type QueryReturnType<T> = UseQueryResult<T, Error> & {
  errorMessage: string | null;
};

export type PaginatedQueryReturnType<T> = QueryReturnType<T> & {
  pagination: Pagination;
  setPagination: React.Dispatch<React.SetStateAction<Pagination>>;
};

export interface UseDataBodyParams<T, F = T, R = EntityQueryResponse<T>> {
  queryKey: string | string[];
  collection: SystemEntityCollections;
  queryFn: (params: {
    offset?: number;
    limit?: number;
    sort?: string;
    params: Partial<F>;
  }) => Promise<EntityQueryResponse<T>>;
  bodyParams?: Partial<F>;
  options?: {
    failSilently?: boolean;
  };
  select?: (data: EntityQueryResponse<T>) => R;
  enabled?: boolean | ((params: Partial<F>) => boolean);
  defaultEntity?: T;
  /**
   * When provided, pagination state is owned by the caller instead of the
   * query hook. Used by useServerFilters so a filter change and page reset
   * can land in the same render (one fetch, not two).
   */
  externalPagination?: Pagination;
  externalSetPagination?: React.Dispatch<React.SetStateAction<Pagination>>;
}

type BaseQueryReturnType<T> = UseQueryResult<T, Error> & {
  errorMessage: string | null;
};

type ExtendedPaginatedQueryReturnType<T> = BaseQueryReturnType<T> & {
  pagination: Pagination;
  setPagination: React.Dispatch<React.SetStateAction<Pagination>>;
};

export const useGenericDataQuery = <T, F = T, R = EntityQueryResponse<T>>(
  {
    collection,
    queryKey,
    queryFn,
    bodyParams = {},
    options = { failSilently: false },
    enabled = true,
    defaultEntity,
    externalPagination,
    externalSetPagination,
  }: UseDataBodyParams<T, F, R>,
  withPagination: boolean = false
): BaseQueryReturnType<R> | ExtendedPaginatedQueryReturnType<R> => {
  const [internalPagination, setInternalPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 100,
  });
  const pagination = externalPagination ?? internalPagination;
  const setPagination = externalSetPagination ?? setInternalPagination;

  const fullQueryKey = [
    collection,
    ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
    bodyParams,
    ...(withPagination ? [pagination] : []),
  ];

  const query = useQuery<EntityQueryResponse<T>, Error, R>({
    queryKey: fullQueryKey,
    enabled: enabled && (typeof enabled === "boolean" || enabled(bodyParams)),
    queryFn: async () => {
      const offset = withPagination
        ? pagination.pageIndex * pagination.pageSize
        : undefined;
      const limit = withPagination ? pagination.pageSize : undefined;
      const sort = withPagination ? pagination.sort : undefined;

      const response = await queryFn({
        offset,
        limit,
        sort,
        params: bodyParams,
      });

      return {
        data:
          (defaultEntity
            ? response.data.map((item) => defaultsDeep(item, defaultEntity))
            : response.data) || [],
        total: response.total || 0,
      };
    },
  });

  const errorMessage = query.isError
    ? `Failed to process fetch ${collection}.`
    : null;

  if (query.isError) {
    if (options.failSilently) {
      console.warn(`Error fetching ${collection}:`, query.error);
    } else {
      console.error(`Error fetching ${collection}:`, query.error);
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  }

  if (withPagination) {
    return {
      ...query,
      errorMessage,
      pagination,
      setPagination,
    };
  }

  return {
    ...query,
    errorMessage,
  };
};

export const useDataQuery = <T, F = T, R = EntityQueryResponse<T>>(
  params: UseDataBodyParams<T, F, R>
) => useGenericDataQuery<T, F, R>(params, false) as QueryReturnType<R>;

export const usePaginatedDataQuery = <T, F = T, R = EntityQueryResponse<T>>(
  params: UseDataBodyParams<T, F, R>
) => useGenericDataQuery<T, F, R>(params, true) as PaginatedQueryReturnType<R>;

interface EntityQueryClientParams<T extends BaseEntity> {
  service: BaseEntityService<T>;
  collection: SystemEntityCollections;
  snackbarDisplayName?: (data: T) => string;
  snackbarAction?: (data: T) => SnackbarAction;
  defaultEntity?: T;
}

interface FetchAllProps<F> {
  enabled?: boolean;
  filters?: Partial<F>;
  externalPagination?: Pagination;
  externalSetPagination?: React.Dispatch<React.SetStateAction<Pagination>>;
}

export const useEntityQueryClient = <T extends BaseEntity, F = T>({
  service,
  collection,
  snackbarDisplayName,
  snackbarAction,
  defaultEntity,
}: EntityQueryClientParams<T>) => {
  const useFetchAll = ({
    enabled = true,
    filters = {},
    externalPagination,
    externalSetPagination,
  }: FetchAllProps<F>) =>
    usePaginatedDataQuery({
      collection,
      queryKey: "all",
      bodyParams: filters,
      defaultEntity,
      enabled: enabled,
      externalPagination,
      externalSetPagination,
      queryFn: ({ offset, limit, sort, params }) =>
        service.fetchAll({ offset, limit, sort, bodyParams: params }),
    });

  const useFetchById = (id: string) => {
    const query = useQuery({
      queryKey: [collection, "byId", id],
      queryFn: async () => {
        const data = await service.fetchById(id);
        return defaultEntity ? defaultsDeep(data, defaultEntity) : data;
      },
      enabled: false,
    });

    const errorMessage = query.isError
      ? `Failed to process fetch ${constructFullId(collection, id)}.`
      : null;

    if (query.isError) {
      console.error(
        `Error fetching ${constructFullId(collection, id)}:`,
        query.error
      );
      enqueueSnackbar(errorMessage, { variant: "error" });
    }

    return {
      ...query,
      errorMessage,
    };
  };

  const useFetchDocumentByFullId = (fullId: string) => {
    const query = useQuery({
      queryKey: [collection, "byFullId", fullId],
      queryFn: async () => {
        const data = await service.fetchDocumentByFullId(fullId);
        return defaultEntity ? defaultsDeep(data, defaultEntity) : data;
      },
      enabled: false,
    });

    const errorMessage = query.isError
      ? `Failed to process fetch ${fullId}.`
      : null;

    if (query.isError) {
      console.error(`Error fetching ${fullId}:`, query.error);
      enqueueSnackbar(errorMessage, { variant: "error" });
    }

    return {
      ...query,
      errorMessage,
    };
  };

  const useCreate = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: Partial<T>) => service.create(data),
      onError: (error: Error) => {
        console.error(`Error creating ${collection}:`, error);
        enqueueSnackbar(`Failed to create ${collection}.`, {
          variant: "error",
        });
      },
      onSuccess: (data: T) => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === collection,
        });
        enqueueSnackbar(
          `Successfully created ${snackbarDisplayName ? snackbarDisplayName(data) : collection}.`,
          {
            variant: "success",
            action: snackbarAction ? snackbarAction(data) : undefined,
          }
        );
      },
    });
  };

  const useUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<T> }) =>
        service.update(id, data),
      onSuccess: (data: T) => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === collection,
        });
        enqueueSnackbar(
          `Successfully updated ${snackbarDisplayName ? snackbarDisplayName(data) : collection}.`,
          {
            variant: "success",
            action: snackbarAction ? snackbarAction(data) : undefined,
          }
        );
      },
    });
  };

  const useDeleteById = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => service.deleteById(id),
      onError: (error: Error | AxiosError) => {
        console.error(`Error deleting ${collection}:`, error);
        const message = getErrorMessage(
          error,
          `Failed to delete ${collection}.`
        );
        enqueueSnackbar(message, {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === collection &&
            !["byId", "byFullId"].includes(query.queryKey[1]),
        });
        enqueueSnackbar(`Successfully deleted ${collection}.`, {
          variant: "success",
        });
      },
    });
  };

  return {
    useFetchAll,
    useFetchById,
    useFetchDocumentByFullId,
    useCreate,
    useUpdate,
    useDeleteById,
  };
};
